import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import {
  type MasterTypePage,
  listAllMasterTypes,
  listMasterTypes,
} from "@/features/master-types/api";

/**
 * Keyset pagination, so an infinite query rather than page numbers.
 *
 * The API returns no `total` — counting defeats a keyset scan, which is
 * the deliberate trade recorded in the PR2 plan. That makes "page 7 of 12"
 * unbuildable and load-more the only honest control.
 */
export function masterTypesQuery(locale: string) {
  return infiniteQueryOptions({
    queryKey: ["master-types", { locale }],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listMasterTypes({ cursor: pageParam, locale }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: MasterTypePage) => page.next_cursor ?? undefined,
  });
}

/**
 * The registry as a lookup table, keyed by code and by id.
 *
 * The hierarchy browser needs both directions: it renders `/records/{code}`
 * links, and `HierarchyNodeOut` identifies a node's type by
 * `master_type_id` rather than by code — a child of a Country is a State,
 * and the tree has to know which route that is.
 */
export function masterTypeIndexQuery(locale: string) {
  return queryOptions({
    queryKey: ["master-types", "index", { locale }],
    queryFn: async () => {
      const page = await listAllMasterTypes(locale);
      return {
        all: page.items,
        byCode: new Map(page.items.map((type) => [type.code, type])),
        byId: new Map(page.items.map((type) => [type.id, type])),
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}
